import { createFileRoute } from "@tanstack/react-router";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, MapPin, Trash2 } from "lucide-react";
import {
  listAppointments,
  saveAppointment,
  setAppointmentDone,
  deleteAppointment,
} from "@/lib/ideas.functions";
import { useBusiness } from "@/components/business-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useOfflineFn } from "@/lib/offline";
import { DateTimePicker } from "@/components/DateTimePicker";

export const Route = createFileRoute("/_authenticated/app/appointments")({
  head: () => ({
    meta: [
      { title: "Appointments — The Founders App" },
      {
        name: "description",
        content: "Keep meetings and calls for each business in one calm list.",
      },
      { property: "og:title", content: "Appointments — The Founders App" },
      {
        property: "og:description",
        content: "Upcoming meetings, calls and commitments per business.",
      },
    ],
  }),
  component: AppointmentsPage,
});

function defaultSlot(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function AppointmentsPage() {
  const { active } = useBusiness();
  const businessId = active?.id ?? "";
  const [title, setTitle] = useState("");
  const [startsAt, setStartsAt] = useState(defaultSlot);
  const [duration, setDuration] = useState(30);
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchAppointments = useServerFn(listAppointments);
  const runSave = useOfflineFn("saveAppointment");
  const runDone = useOfflineFn("setAppointmentDone");
  const runDelete = useOfflineFn("deleteAppointment");

  const query = useQuery({
    queryKey: ["appointments", businessId],
    enabled: Boolean(businessId),
    queryFn: () => fetchAppointments({ data: { businessId } }),
  });

  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const rows = query.data ?? [];
    return {
      upcoming: rows.filter((a) => new Date(a.starts_at).getTime() >= now && !a.done),
      past: rows
        .filter((a) => new Date(a.starts_at).getTime() < now || a.done)
        .sort((a, b) => new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime()),
    };
  }, [query.data]);

  const add = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      await runSave({
        data: { businessId, title, location, notes, startsAt, durationMinutes: duration },
      });
      setTitle("");
      setLocation("");
      setNotes("");
      setStartsAt(defaultSlot());
      void query.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const row = (a: (typeof upcoming)[number], muted = false) => (
    <article key={a.id} className={cn("soft-panel p-5", muted && "opacity-70")}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="micro-label">
            {new Date(a.starts_at).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })}{" "}
            · {a.duration_minutes}m
          </p>
          <h3 className={cn("notranslate mt-2 font-medium break-words", a.done && "line-through")}>{a.title}</h3>
          {a.location && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" /> <span className="notranslate">{a.location}</span>
            </p>
          )}
          {a.notes && (
            <p className="notranslate mt-2 text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
              {a.notes}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <button
            aria-label={a.done ? "Mark as not done" : "Mark as done"}
            onClick={async () => {
              await runDone({ data: { id: a.id, done: !a.done } });
              void query.refetch();
            }}
            className={cn(
              "transition-colors",
              a.done ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Check className="h-4 w-4" />
          </button>
          <ConfirmDelete
            ariaLabel="Delete appointment"
            itemLabel="this appointment"
            onConfirm={async () => {
              await runDelete({ data: { id: a.id } });
              void query.refetch();
            }}
          >
            <Trash2 className="h-4 w-4" />
          </ConfirmDelete>
        </div>
      </div>
    </article>
  );

  if (!businessId) return null;

  return (
    <div className="space-y-8">
      <section className="vault-panel p-7">
        <p className="micro-label">Appointments</p>
        <h2 className="mt-3 text-xl font-medium">What's on the calendar</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Calls, meetings and commitments for <span className="notranslate">{active?.name}</span>.
        </p>
        <Input
          className="mt-4"
          placeholder="Call with supplier"
          value={title}
          maxLength={200}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="appt-when" className="micro-label">
              Date &amp; time
            </label>
            <DateTimePicker id="appt-when" value={startsAt} onChange={setStartsAt} />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="appt-duration" className="micro-label">
              Duration (minutes)
            </label>
            <div className="relative">
              <Input
                id="appt-duration"
                type="number"
                min={5}
                max={1440}
                step={5}
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="pr-12"
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
                min
              </span>
            </div>
          </div>
        </div>
        <Input
          className="mt-3"
          placeholder="Where — office, Zoom, phone…"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
        />
        <Textarea
          className="mt-3 min-h-20 resize-none"
          placeholder="Anything to prepare?"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <Button className="sheen mt-5" disabled={busy} onClick={() => void add()}>
          Add appointment
        </Button>
      </section>

      <section className="space-y-4">
        <p className="micro-label">Upcoming</p>
        {upcoming.map((a) => row(a))}
        {upcoming.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing scheduled yet. Add the next thing that's actually booked.</p>
        )}
      </section>

      {past.length > 0 && (
        <section className="space-y-4">
          <p className="micro-label">Past & done</p>
          {past.map((a) => row(a, true))}
        </section>
      )}
    </div>
  );
}
